import {
    AfterViewInit,
    Directive,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
    inject
} from "@angular/core";
import { ObjectUtils } from "~app/common/utils/object-utils";

/**
 * Original source -> https://github.com/phuoc-ng/html-dom/tree/master/demo/drag-and-drop-table-row
 */
@Directive({ selector: "[raskRowDrag]" })
export class RowDragDirective implements OnChanges, AfterViewInit  {
    @Input("raskRowDrag") data: any[];

    private readonly dragHandleQuery = "div.drag-handle";
    private readonly draggableQuery = "div.draggable";
    private readonly el = inject(ElementRef);

    private draggingEle;
    private draggingRowIndex: number;
    private isDraggingStarted = false;
    private list: HTMLDivElement;
    private nativeElement: HTMLDivElement;
    private placeholder: HTMLDivElement;

    // The current position of mouse relative to the dragging element
    private x = 0;
    private y = 0;

    cloneTable = () => {
        const width = parseInt(window.getComputedStyle(this.nativeElement).width, 10);

        this.list = document.createElement("div");
        this.list.classList.add("clone-list");
        this.list.style.position = "absolute";
        this.list.style.left = `${this.nativeElement.offsetLeft}px`;
        this.list.style.top = `${this.nativeElement.offsetTop}px`;
        this.list.style.width = `${width}px`;
        this.nativeElement.parentNode.insertBefore(this.list, this.nativeElement);

        // Hide the original table
        this.nativeElement.style.visibility = "hidden";

        this.nativeElement.querySelectorAll(this.draggableQuery).forEach((row) => {
            const clone = row.cloneNode(true) as any;
            clone.style.width = `${width}px`;
            this.list.appendChild(clone);
        });
    };

    // Check if `nodeA` is above `nodeB`
    isAbove = (nodeA, nodeB) => {
        // Get the bounding rectangle of nodes
        const rectA = nodeA.getBoundingClientRect();
        const rectB = nodeB.getBoundingClientRect();

        return (rectA.top + rectA.height / 2 < rectB.top + rectB.height / 2);
    };

    // Swap two nodes
    swap = (nodeA, nodeB) => {
        const parentA = nodeA.parentNode;
        const siblingA = nodeA.nextSibling === nodeB ? nodeA : nodeA.nextSibling;

        // Move `nodeA` to before the `nodeB`
        nodeB.parentNode.insertBefore(nodeA, nodeB);

        // Move `nodeB` to before the sibling of `nodeA`
        parentA.insertBefore(nodeB, siblingA);
    };

    mouseMoveHandler = (e) => {
        if (!this.isDraggingStarted) {
            this.isDraggingStarted = true;

            this.cloneTable();

            this.draggingEle = [].slice.call(this.list.children)[this.draggingRowIndex];
            this.draggingEle.classList.add("dragging");
            this.draggingEle.style.left = `${this.draggingEle.offsetLeft + e.clientX - this.x + 10}px`;

            // Let the placeholder take the height of dragging element
            // So the next element won't move up
            this.placeholder = document.createElement("div");
            this.placeholder.classList.add("placeholder");
            this.draggingEle.parentNode.insertBefore(this.placeholder, this.draggingEle.nextSibling);
            this.placeholder.style.height = `${this.draggingEle.offsetHeight}px`;
        }

        // Set position for dragging element
        this.draggingEle.style.position = "absolute";
        this.draggingEle.style.top = `${this.draggingEle.offsetTop + e.clientY - this.y}px`;

        // Reassign the position of mouse
        this.x = e.clientX;
        this.y = e.clientY;

        // The current order
        // prevEle
        // draggingEle
        // placeholder
        // nextEle
        const prevEle = this.draggingEle.previousElementSibling;
        const nextEle = this.placeholder.nextElementSibling;

        // The dragging element is above the previous element
        // User moves the dragging element to the top
        // We don't allow to drop above the header
        // (which doesn't have `previousElementSibling`)
        if (prevEle && this.isAbove(this.draggingEle, prevEle)) {
            // The current order    -> The new order
            // prevEle              -> placeholder
            // draggingEle          -> draggingEle
            // placeholder          -> prevEle
            this.swap(this.placeholder, this.draggingEle);
            this.swap(this.placeholder, prevEle);
            return;
        }

        // The dragging element is below the next element
        // User moves the dragging element to the bottom
        if (nextEle && this.isAbove(nextEle, this.draggingEle)) {
            // The current order    -> The new order
            // draggingEle          -> nextEle
            // placeholder          -> placeholder
            // nextEle              -> draggingEle
            this.swap(nextEle, this.placeholder);
            this.swap(nextEle, this.draggingEle);
        }
    };

    mouseUpHandler = () => {
        if (!this.list || !this.list.parentNode) {
            // Mouse up triggered before mouse move occured
            this.onAfterMouseUp();
            return;
        }

        // Remove the placeholder
        if (this.placeholder && this.placeholder.parentNode) {
            this.placeholder.parentNode.removeChild(this.placeholder);
        }

        this.draggingEle.classList.remove("dragging");
        this.draggingEle.style.removeProperty("top");
        this.draggingEle.style.removeProperty("left");
        this.draggingEle.style.removeProperty("position");

        // Get the end index
        const endRowIndex = [].slice.call(this.list.children).indexOf(this.draggingEle);

        // Remove the `list` element
        this.list.parentNode.removeChild(this.list);

        // Move the dragged row to `endRowIndex`
        const source = this.data[this.draggingRowIndex];
        const rows = [].slice.call(this.nativeElement.querySelectorAll(this.draggableQuery));
        if (this.draggingRowIndex > endRowIndex) {
            rows[endRowIndex].parentNode.insertBefore(rows[this.draggingRowIndex], rows[endRowIndex]);
            this.data.splice(this.draggingRowIndex, 1);
            this.data.splice(endRowIndex, 0, source);
        } else {
            rows[endRowIndex].parentNode.insertBefore(rows[this.draggingRowIndex], rows[endRowIndex].nextSibling);
            this.data.splice(this.draggingRowIndex, 1);
            this.data.splice(endRowIndex, 0, source);
        }

        this.onAfterMouseUp();
    };

    mouseDownHandler = (e) => {
        // Get the original row
        const originalRow = this.findRow(e.target);
        if (!originalRow) {
            return;
        }

        this.draggingRowIndex = [].slice.call(this.nativeElement.querySelectorAll(this.draggableQuery)).indexOf(originalRow);

        // Determine the mouse position
        this.x = e.clientX;
        this.y = e.clientY;

        // Attach the listeners to `document`
        document.addEventListener("mousemove", this.mouseMoveHandler);
        document.addEventListener("mouseup", this.mouseUpHandler);
    };

    ngAfterViewInit(): void {
        if (!this.data) {
            return;
        }

        this.updateView();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.data && !changes.data.firstChange) {
            this.updateView();
        }
    }

    private findRow(element: HTMLElement): HTMLElement {
        if (!element) {
            return undefined;
        }

        if (ObjectUtils.isString(element.className) && element.className.indexOf("draggable") > -1) {
            return element;
        }

        return this.findRow(element.parentElement);
    }

    private onAfterMouseUp(): void {
        this.isDraggingStarted = false;

        // Bring back the table
        this.nativeElement.style.removeProperty("visibility");

        // Remove the handlers of `mousemove` and `mouseup`
        document.removeEventListener("mousemove", this.mouseMoveHandler);
        document.removeEventListener("mouseup", this.mouseUpHandler);
    }

    private updateView(): void {
        if (this.list && this.list.parentNode) {
            this.list.parentNode.removeChild(this.list);
        }

        setTimeout(() => {
            this.nativeElement = this.el.nativeElement.querySelector(".body");

            this.nativeElement.querySelectorAll(this.dragHandleQuery).forEach((div, index) => {
                div.addEventListener("mousedown", this.mouseDownHandler);
            });
        }, 0);
    }
}
